import React from 'react';
import { Container, Typography, Box, Grid, Card, CardContent, LinearProgress, Chip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  School, 
  EmojiEvents, 
  Code, 
  Psychology,
  TrendingUp,
  Language,
  StarRate,
  WorkspacePremium
} from '@mui/icons-material';

const StyledCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
  border: '1px solid rgba(102, 126, 234, 0.1)',
  borderRadius: '20px',
  padding: '20px',
  height: '100%',
  transition: 'all 0.3s ease',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(102, 126, 234, 0.15)',
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: 60,
  height: 60,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
  marginBottom: '20px',
  boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)',
}));

const SkillProgress = styled(Box)(({ theme }) => ({
  marginBottom: '16px',
}));

const StyledLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 8,
  borderRadius: 5,
  backgroundColor: 'rgba(102, 126, 234, 0.1)',
  '& .MuiLinearProgress-bar': {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: 5,
  },
}));

const AchievementChip = styled(Chip)(({ theme }) => ({
  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  color: 'white',
  fontWeight: 600,
  margin: '4px',
  boxShadow: '0 4px 15px rgba(240, 147, 251, 0.3)',
  '&:hover': {
    transform: 'scale(1.05)',
  },
  transition: 'all 0.3s ease',
}));

const skills = [
  { name: 'Python', level: 95 },
  { name: 'Machine Learning & Deep Learning', level: 90 },
  { name: 'Graph Neural Networks', level: 88 },
  { name: 'PyTorch / TensorFlow', level: 86 },
  { name: 'React.js / TypeScript', level: 85 },
  { name: 'Node.js / Django', level: 82 },
  { name: 'Knowledge Graphs (Neo4j)', level: 80 },
  { name: 'LLMs, RAG & Graph RAG', level: 84 },
];

const achievements = [
  'Q1 Journal Publication (Int. J. of Climatology)',
  '1st Author — 3 Research Papers',
  '4th Runner Up - Techgig Geek Goddess 2022',
  '4th Place - Ada Lovelace Contest 2022',
  'Code Samurai 2024 Finalist',
  'ICPC Asia Dhaka Regional Participant',
];

const About = () => {
  return (
    <Container
      id="about"
      maxWidth="xl"
      sx={{ 
        padding: '80px 0',
        background: 'linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%)',
      }}
    >
      <Box textAlign="center" sx={{ marginBottom: 6 }}>
        <Typography 
          variant="h3" 
          component="h2"
          sx={{ 
            fontWeight: 700,
            marginBottom: 2,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          About Me
        </Typography>
        <Typography 
          variant="h6" 
          sx={{ 
            color: '#666', 
            maxWidth: '800px', 
            margin: '0 auto',
            lineHeight: 1.8,
          }}
        >
          Computer Science graduate and M.Sc. researcher with a stellar academic record, blending
          cutting-edge AI/ML research with hands-on full-stack engineering to solve impactful real-world problems.
        </Typography>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <StyledCard>
            <IconWrapper>
              <School fontSize="large" />
            </IconWrapper>
            <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: 2 }}>
              Academic Excellence
            </Typography>
            <Typography sx={{ marginBottom: 2, color: '#555', lineHeight: 1.7 }}>
              Completed B.Sc. in Computer Science and Engineering at Jahangirnagar University with an
              outstanding CGPA of 3.99/4.00, and currently pursuing an M.Sc. in CSE with a perfect
              4.00/4.00 (until 1st Semester). Committed to academic excellence alongside active research
              and competitive programming.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StarRate sx={{ color: '#f5576c' }} />
              <Typography sx={{ fontWeight: 600, color: '#667eea' }}>
                B.Sc. 3.99/4.00 · M.Sc. 4.00/4.00 (until 1st Sem.)
              </Typography>
            </Box>
          </StyledCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <StyledCard>
            <IconWrapper>
              <Code fontSize="large" />
            </IconWrapper>
            <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: 2 }}>
              Technical Skills
            </Typography>
            <Typography sx={{ marginBottom: 3, color: '#555', lineHeight: 1.7 }}>
              Proficient across the stack — from training deep learning and graph neural network models
              in PyTorch/TensorFlow to shipping full-stack web apps and AI agents with React, Node.js, and LLMs.
            </Typography>
            <Box>
              {skills.slice(0, 4).map((skill, index) => (
                <SkillProgress key={index}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {skill.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#667eea', fontWeight: 600 }}>
                      {skill.level}%
                    </Typography>
                  </Box>
                  <StyledLinearProgress variant="determinate" value={skill.level} />
                </SkillProgress>
              ))}
            </Box>
          </StyledCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <StyledCard>
            <IconWrapper>
              <EmojiEvents fontSize="large" />
            </IconWrapper>
            <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: 2 }}>
              Achievements & Awards
            </Typography>
            <Typography sx={{ marginBottom: 3, color: '#555', lineHeight: 1.7 }}>
              Consistent performer in national and international programming contests with multiple 
              awards and recognitions in competitive programming and research.
            </Typography>
            <Box>
              {achievements.map((achievement, index) => (
                <AchievementChip 
                  key={index}
                  label={achievement}
                  size="small"
                />
              ))}
            </Box>
          </StyledCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <StyledCard>
            <IconWrapper>
              <Psychology fontSize="large" />
            </IconWrapper>
            <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: 2 }}>
              Research & Innovation
            </Typography>
            <Typography sx={{ marginBottom: 2, color: '#555', lineHeight: 1.7 }}>
              First-author researcher across three papers spanning machine learning for climate, temporal
              graph learning for financial fraud detection, and knowledge-graph question answering for the
              low-resource Bangla language.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 2 }}>
              <WorkspacePremium sx={{ color: '#f5576c' }} />
              <Typography sx={{ fontWeight: 600, color: '#667eea' }}>
                Q1 Journal Publication · 2 Papers in Review
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: '#555', fontStyle: 'italic' }}>
              "Thunderstorm Forecasting using Machine Learning Techniques" — International Journal of Climatology (Q1)
            </Typography>
          </StyledCard>
        </Grid>
      </Grid>

      {/* <Box sx={{ marginTop: 6, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: 3 }}>
          Core Competencies
        </Typography>
        <Grid container spacing={2}>
          {skills.map((skill, index) => (
            <Grid item xs={6} sm={4} md={3} key={index}>
              <SkillProgress>
                <Typography variant="body2" sx={{ fontWeight: 600, marginBottom: 1, color: 'black' }}>
                  {skill.name}
                </Typography>
                <StyledLinearProgress variant="determinate" value={skill.level} />
                <Typography variant="caption" sx={{ color: '#667eea', fontWeight: 600 }}>
                  {skill.level}%
                </Typography>
              </SkillProgress>
            </Grid>
          ))}
        </Grid>
      </Box> */}
    </Container>
  );
};

export default About;
